import { HTMLProps, useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toPrice } from '../../../../utils/parsers';
import { Button } from '../../../general/Button';
import { Account, AccountType, PensionContext } from '../../../../contexts/pension-robot/PensionContext';
import { IconElements } from '../../../../config/icons';
import { SupplementGeneralModal } from '../../supplement-overlay/SupplementGeneralModal';

export const SaveOrInvest: React.FC<HTMLProps<HTMLDivElement>> = () => {
    const {
        actions, parameters: {supplementInfo}
    } = useContext(PensionContext);
    const [accountType, setAccountType] = useState<AccountType>("saving");
    const [showModal, setShowModal] = useState(false);

    const getAccount = (accountType: AccountType) => {
        if (accountType === "investment" )
            return supplementInfo?.self?.additionalInvestmentAccount?.isUsed
                ? supplementInfo?.self?.additionalInvestmentAccount?.account
                : { type: "investment", 
                    recurringPaymentInterval: 'month',
                    riskProfile: 'neutraal' } as Account;
        if (accountType === "saving" )
            return supplementInfo?.self?.additionalSavingAccount?.isUsed
                ? supplementInfo?.self?.additionalSavingAccount?.account
                : { type: "saving", 
                    recurringPaymentInterval: 'month',
                    riskProfile: 'neutraal' } as Account;
        return undefined;
    }

    const addAccount = (account: Account) => {
        if (account.type === "investment") 
            actions.setParameters({ supplementInfo: { self: { additionalInvestmentAccount: {
                account,
                isUsed: true,
            }} } });

        if (account.type === "saving")
            actions.setParameters({ supplementInfo: { self: { additionalSavingAccount: {
                account,
                isUsed: true
            }} } });
    }

    const currentAccountInvestment = getAccount('investment');
    const currentAccountSaving = getAccount('saving');
    const selectedAccount = accountType === "investment" ? currentAccountInvestment : currentAccountSaving;

    return (
        <div className="p-4 border-b flex flex-col gap-6">
            <StartInformation
                    setShowModal={setShowModal}
                    setModalShowInvestment={setAccountType}
                    currentAccountInvestmentActive={!!supplementInfo?.self?.additionalInvestmentAccount?.isUsed}
                    currentAccountSavingActive={!!supplementInfo?.self?.additionalSavingAccount?.isUsed}
            />
            { supplementInfo?.self?.additionalInvestmentAccount?.isUsed
                && currentAccountInvestment
                && <AddedInformation 
                    currentAccount={currentAccountInvestment}
                    setModalShowInvestment={setAccountType}
                    setShowModal={setShowModal}
                />}
            { supplementInfo?.self?.additionalSavingAccount?.isUsed
                && currentAccountSaving
                && <AddedInformation
                    currentAccount={currentAccountSaving}
                    setModalShowInvestment={setAccountType}
                    setShowModal={setShowModal}
                />}
            { showModal 
                && !!selectedAccount
                && <SupplementGeneralModal
                    onlyAvailableAtPensionDate={true}
                    isPartnerSection={false}
                    account={selectedAccount}
                    onClose= { () => setShowModal(false) }
                    isShown={showModal}
                    updateAccount={addAccount}
                />
            }
        </div>
    );
};

const StartInformation: React.FC<{
    setShowModal: (isShown: boolean) => void,
    setModalShowInvestment: (newState: AccountType) => void,
    currentAccountInvestmentActive: boolean,
    currentAccountSavingActive: boolean
}> = ({ setShowModal, setModalShowInvestment, currentAccountInvestmentActive, currentAccountSavingActive }) => {
    const { t } = useTranslation('pension');

    const getButtonAccount = (active: boolean, investmentType: AccountType) => {
        return <Button
            className={`w-48 font-semibold ${active && 'opacity-50'}`}
            tint="green"
            size="sm"
            disabled={active}
            onClick={() => {
                setModalShowInvestment(investmentType);
                setShowModal(true);
            }}
        >
            { investmentType === "investment"
                ? t("pension-cocktail-fill-screen-results-save-or-invest-btn-invest")
                : t("pension-cocktail-fill-screen-results-save-or-invest-btn-save")
            }
        </Button>
    }

    return (
        <div className="flex flex-col gap-4 sm:gap-6">
            <div>{t("pension-cocktail-fill-screen-results-save-or-invest-text")}</div>
            <div>
                <Trans
                    t={t}
                    i18nKey="pension-cocktail-fill-screen-results-save-or-invest-question"
                    values={{proefberekening: "proefberekening"}}
                >
                    ... <span className="font-bold text-bnd-pink">proefberekening</span> ...
                </Trans>
            </div>
            <div className="rounded-lg bg-bnd-white-1 shadow-xl p-5 flex flex-row flex-wrap gap-2 sm:gap-4 justify-start">
                {getButtonAccount(currentAccountInvestmentActive, 'investment')}
                {getButtonAccount(currentAccountSavingActive, 'saving')}
            </div>
        </div>
    );
}

const AddedInformation: React.FC<{
    setShowModal: (isShown: boolean) => void,
    setModalShowInvestment: (newState: AccountType) => void,
    currentAccount: Account}> 
= ({ setShowModal, currentAccount, setModalShowInvestment}) => {
    const { t } = useTranslation('pension');
    const { actions, parameters } = useContext(PensionContext);
    const [iconIsUp, setIconIsUp] = useState(true);

    const onRemoveAccount = () => {
        if (currentAccount.type === 'investment'){
            actions.setParameters({ supplementInfo: { self: { additionalInvestmentAccount: {isUsed: false} }}});
        } else {
            actions.setParameters({ supplementInfo: { self: { additionalSavingAccount: {isUsed: false} }}});
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div>
                <Trans
                    t={t}
                    i18nKey={currentAccount.type === 'saving' 
                        ? "pension-cocktail-fill-screen-results-save-or-invest-info-save" 
                        : "pension-cocktail-fill-screen-results-save-or-invest-info-invest"}
                    values={{value: toPrice((currentAccount.predictedAmountMonthly ?? 0), {
                        onlyFullEuros: true,
                        withCurrencySymbol: true,
                    })}}
                >
                    ... <span className="font-bold">value</span> ...
                </Trans>
            </div>
            <div className="rounded-lg bg-bnd-white-1 shadow-xl p-5 flex flex-col justify-start">
                <div className="flex flex-row justify-between border-b ">
                    <div className="font-bold">
                        {t("pension-cocktail-fill-screen-results-save-or-invest-info-exp-returns")}
                    </div>
                    <div className="mr-4" onClick={() => setIconIsUp(old => !old)}>
                        <IconElements.ChevronDown
                            className={`text-bnd-green h-4 w-4 transition cursor-pointer ${iconIsUp ? '' : 'transform rotate-180'}`}
                        />
                    </div>
                </div>
                {!iconIsUp && <>
                    <div className="my-4 flex flex-col">
                        <div>
                            <Trans
                                t={t}
                                i18nKey={"pension-cocktail-fill-screen-results-save-or-invest-info-start-investment"}
                                values={{
                                    value: toPrice((currentAccount.currentAmount ?? 0), {
                                        onlyFullEuros: true,
                                        withCurrencySymbol: true})
                                }}
                            >
                                ... <span className="font-bold">value</span> ...
                            </Trans>
                        </div>
                        <div>
                            <Trans
                                t={t}
                                i18nKey={"pension-cocktail-fill-screen-results-save-or-invest-info-investment"}
                                values={{
                                    value: toPrice((currentAccount.recurringPaymentAmount ?? 0), {
                                        onlyFullEuros: true,
                                        withCurrencySymbol: true}),
                                    period: currentAccount.recurringPaymentInterval === "month" 
                                        ? t("general-per-month-lower") 
                                        : t("general-per-year-lower")
                                }}
                            >
                                ... <span className="font-bold">value</span> ...
                            </Trans>
                        </div>
                        {currentAccount.type === 'investment' &&  <div> 
                            <Trans
                                t={t}
                                i18nKey={"pension-cocktail-fill-screen-results-save-or-invest-info-risk-strategy"}
                                values={{value: currentAccount.riskProfile}}
                            >
                                ... <span className="font-bold">value</span> ...
                            </Trans>
                        </div>}
                    </div>
                    <div className="flex flex-col">
                        <div className="font-bold">
                            {t("pension-cocktail-fill-screen-results-save-or-invest-info-returns-pension", {year: 2000})}
                        </div>
                        <div>
                            <Trans
                                t={t}
                                i18nKey={"pension-cocktail-fill-screen-results-save-or-invest-info-returns-text"}
                                values={{
                                    year: parameters.personalInfo.self?.pensionAge && !!parameters.personalInfo.self?.age 
                                        ? parameters.personalInfo.self?.pensionAge - parameters.personalInfo.self?.age
                                        : 0,
                                    totalValue: toPrice((currentAccount.predictedAmount ?? 0), {
                                        onlyFullEuros: true,
                                        withCurrencySymbol: true,
                                    }),
                                    monthlyValue: toPrice((currentAccount.predictedAmountMonthly ?? 0), {
                                        onlyFullEuros: true,
                                        withCurrencySymbol: true,
                                    })
                                }}
                            >
                                ... 
                                <span className="font-bold">year</span>
                                <span className="font-bold">totalValue</span>
                                <span className="font-bold">monthlyValue</span> 
                                ...
                            </Trans>
                        </div>
                    </div>
                </>}
                <div className="mt-4 flex flex-row flex-wrap gap-2 sm:gap-4">
                    <Button
                        tint="green"
                        className="w-48"
                        size="sm"
                        onClick={() => {
                            setModalShowInvestment(currentAccount.type)
                            setShowModal(true)
                        }}
                    >
                        {t("pension-cocktail-fill-screen-results-save-or-invest-info-edit-account")}
                    </Button>
                    <Button
                        tint="neutral-no-hover"
                        className="w-48"
                        size="sm"
                        onClick={onRemoveAccount}
                    >
                        {t("pension-cocktail-fill-screen-results-save-or-invest-info-remove-account")}
                    </Button>
                </div>
            </div>
        </div>
    );
};